import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FileText, Search, ExternalLink, Trash2, RotateCcw,
  ChevronFirst, ChevronLeft, ChevronRight, ChevronLast,
} from "lucide-react";
import { formatInTimeZone } from "date-fns-tz";
import { PageHeader, PageShell } from "@/components/PageHeader";
import { SectionHeader } from "@/components/SectionHeader";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

const TZ = "America/Chicago";

// RC10r · timezone-aware formatters. CST / CDT auto-resolved by date.
function formatCreatedOn(iso) {
  if (!iso) return "";
  try {
    return formatInTimeZone(new Date(iso), TZ, "MM/dd/yyyy h:mm a");
  } catch { return ""; }
}
function formatCreatedOnFull(iso) {
  if (!iso) return "";
  try {
    return formatInTimeZone(new Date(iso), TZ, "MM/dd/yyyy h:mm:ss a zzz");
  } catch { return ""; }
}

// RC10r Pass 2 · DI07 — single source of truth for the "(Former User)"
// label. Backend returns bare `display_name` + `is_former_user:bool`.
// Frontend composes the suffix EXACTLY ONCE from the flag.
function composeCreatedBy(name, isFormer) {
  const raw = (name || "").trim();
  if (isFormer) {
    return raw ? `${raw} (Former User)` : "(Former User)";
  }
  return raw || "Unknown — Legacy Document";
}

// RC10r · sort options exposed via the URL `?sort=` param.
const SORT_OPTIONS = [
  { value: "created_on_desc", label: "Created On — newest first" },
  { value: "created_on_asc",  label: "Created On — oldest first" },
  { value: "created_by_asc",  label: "Created By — A–Z" },
  { value: "title_asc",       label: "Document Name — A–Z" },
];
const DEFAULT_SORT = "created_on_desc";

// RC10r Pass 2 · DI20/DI24 — pagination constants (RC10n pattern).
const PAGE_SIZE_OPTIONS = ["10", "25", "50", "100"];
const DEFAULT_PAGE_SIZE = "25";

const TYPE_LABEL = {
  quote_pdf: "Quote",
  contract_pdf: "Contract",
  pickup_sheet_pdf: "Pickup Sheet",
  other: "Other",
  coi: "COI",
  invoice: "Invoice",
};

// RC5 Part 2 · ITEM 5 — protected-type detection mirrors backend.
const PROTECTED_CONTRACT_STATUSES = new Set(["Executed", "executed"]);
const PROTECTED_QUOTE_STATUSES = new Set([
  "Sent", "Accepted", "Superseded", "Executed",
  "sent", "accepted", "superseded", "executed",
]);
function protectedReasonFor(row) {
  if (!row) return null;
  const dtype = (row.doc_type || row.document_type || row.type || "").toLowerCase();
  const status = row.status || "";
  if (dtype === "contract" && PROTECTED_CONTRACT_STATUSES.has(status)) return "executed_contract";
  if (dtype === "quote" && PROTECTED_QUOTE_STATUSES.has(status)) return "finalized_quote";
  if (dtype === "invoice") return "invoice";
  if (row.is_snapshot) return "immutable_snapshot";
  if (row.is_system_template || row.owner_type === "system") return "system_template";
  return null;
}
const PROTECTED_TYPE_LABEL = {
  executed_contract: "contract",
  finalized_quote: "quote",
  invoice: "invoice",
  immutable_snapshot: "snapshot",
  system_template: "system template",
};

export default function DocumentsPage() {
  const { isSuperAdmin, isAdmin } = useAuth();
  const canManageArchive = Boolean(isSuperAdmin || isAdmin);
  const [rows, setRows] = useState(null);
  const [envelope, setEnvelope] = useState({
    total_items: 0, total_pages: 1,
    has_previous: false, has_next: false,
    page: 1, page_size: 25,
  });
  const [busyId, setBusyId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [confirmDoc, setConfirmDoc] = useState(null);

  // RC10r · URL-backed state. Sort + search + type + tab + pagination
  // round-trip on refresh.
  const [searchParams, setSearchParams] = useSearchParams();
  const sortParamRaw = searchParams.get("sort");
  const sort = SORT_OPTIONS.some((o) => o.value === sortParamRaw)
    ? sortParamRaw
    : DEFAULT_SORT;
  const q = searchParams.get("search") || "";
  const type = searchParams.get("document_type") || "";
  const tab = searchParams.get("tab") === "deleted" ? "deleted" : "active";
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);
  const pageSize = useMemo(() => {
    const raw = searchParams.get("page_size");
    return PAGE_SIZE_OPTIONS.includes(raw) ? raw : DEFAULT_PAGE_SIZE;
  }, [searchParams]);

  // RC10r Pass 2 · DI19 — StrictMode guard uses previous-value ref
  // comparison instead of a dependency-triggered reset effect. The
  // previous implementation reset `lastFetchKey.current = null`
  // inside an effect keyed on `reloadKey`, which StrictMode re-ran
  // on the second dev-time mount AFTER the guard had been set,
  // clearing it before `load()` returned — producing two network
  // calls on fresh mount. Pass 2 flips the strategy: the guard is
  // cleared ONLY inside the delete/restore handlers (the only
  // callers that bump `reloadKey`), never from an effect.
  const lastFetchKey = useRef(null);

  const updateParams = useCallback((patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === "" || v === null || v === undefined) {
        next.delete(k);
      } else {
        next.set(k, String(v));
      }
    });
    setSearchParams(next, { replace: false });
  }, [searchParams, setSearchParams]);

  const setSort = (v) => {
    const next = new URLSearchParams(searchParams);
    if (v === DEFAULT_SORT) next.delete("sort"); else next.set("sort", v);
    // Changing sort resets to page 1.
    next.delete("page");
    setSearchParams(next, { replace: false });
  };
  const setQ = (v) => {
    const next = new URLSearchParams(searchParams);
    if (!v) next.delete("search"); else next.set("search", v);
    next.delete("page");
    setSearchParams(next, { replace: false });
  };
  const setType = (v) => {
    const next = new URLSearchParams(searchParams);
    if (!v) next.delete("document_type"); else next.set("document_type", v);
    next.delete("page");
    setSearchParams(next, { replace: false });
  };
  const setTab = (v) => {
    const next = new URLSearchParams(searchParams);
    if (v === "active") next.delete("tab"); else next.set("tab", v);
    next.delete("page");
    setSearchParams(next, { replace: false });
  };
  const setPage = (p) => updateParams({ page: p });
  const setPageSize = (ps) => updateParams({ page_size: ps, page: 1 });

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (type) params.set("document_type", type);
    if (q) params.set("q", q);
    if (tab === "deleted") params.set("archived", "true");
    params.set("sort", sort);
    params.set("page", String(page));
    params.set("page_size", pageSize);
    const key = params.toString();
    if (lastFetchKey.current === key) return;
    lastFetchKey.current = key;
    setRows(null);
    api
      .get(`/documents-center/all?${params.toString()}`)
      .then((r) => {
        const d = r.data || {};
        const items = d.items || d.results || [];
        setRows(items);
        setEnvelope({
          total_items: d.total_items ?? d.total ?? items.length,
          total_pages: d.total_pages ?? 1,
          has_previous: !!d.has_previous,
          has_next: !!d.has_next,
          page: d.page ?? page,
          page_size: d.page_size ?? parseInt(pageSize, 10),
        });
        // Bounce out-of-range page (e.g. after delete).
        const tp = d.total_pages ?? 1;
        if (items.length === 0 && page > 1 && page > tp) {
          setPage(tp);
        }
      })
      .catch(() => {
        setRows([]);
        setEnvelope({
          total_items: 0, total_pages: 1,
          has_previous: false, has_next: false,
          page: 1, page_size: parseInt(pageSize, 10),
        });
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, type, tab, sort, page, pageSize]);

  useEffect(() => { load(); }, [load, reloadKey]);

  useEffect(() => {
    if (tab === "deleted" && !canManageArchive) setTab("active");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, canManageArchive]);

  const openFile = async (fileId) => {
    try {
      const r = await api.get(`/files/${fileId}/download-token`);
      const t = r.data?.token;
      if (t) {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${fileId}?token=${t}`;
        window.open(url, "_blank", "noopener");
      }
    } catch (e) {
      toast.error("Could not open file");
    }
  };

  const performDelete = async (doc) => {
    setBusyId(doc.id);
    try {
      const r = await api.delete(`/documents/${doc.id}`);
      const mode = r?.data?.mode;
      toast.success(mode === "hide" ? "Document removed" : "Document deleted");
      // DI19 — clear the fetch guard directly before bumping reloadKey.
      lastFetchKey.current = null;
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error("Delete failed");
    } finally {
      setBusyId(null);
      setConfirmDoc(null);
    }
  };

  const requestDelete = (doc) => setConfirmDoc(doc);

  const restoreDoc = async (doc) => {
    setBusyId(doc.id);
    try {
      await api.post(`/documents/${doc.id}/restore`);
      toast.success("Document restored");
      lastFetchKey.current = null;
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error("Restore failed");
    } finally {
      setBusyId(null);
    }
  };

  const isDeletedTab = tab === "deleted";
  const confirmProtected = confirmDoc ? protectedReasonFor(confirmDoc) : null;
  const confirmProtectedLabel = confirmProtected ? PROTECTED_TYPE_LABEL[confirmProtected] : null;

  const firstRow = envelope.total_items === 0 ? 0 : (envelope.page - 1) * envelope.page_size + 1;
  const lastRow = Math.min(envelope.page * envelope.page_size, envelope.total_items);

  return (
      <PageShell>
        <PageHeader
          testid="page-documents"
          title={<span className="inline-flex items-center gap-2"><FileText size={22} className="text-coach-blue" />Documents Center</span>}
          subtitle="Every generated PDF (quotes, contracts, pickup sheets) and uploaded document indexed in one place. Portal customers see only what has been explicitly shared with them."
        />

        <div className="mb-3 border-b border-coach-silver/60 flex items-end gap-1" data-testid="documents-tabs">
          <button
            type="button"
            onClick={() => setTab("active")}
            data-testid="documents-tab-active"
            className={`px-3 py-1.5 text-[12px] uppercase tracking-widest border-b-2 -mb-px ${
              !isDeletedTab
                ? "border-coach-blue text-carbon"
                : "border-transparent text-steel hover:text-carbon"
            }`}
          >
            Active
          </button>
          {canManageArchive && (
            <button
              type="button"
              onClick={() => setTab("deleted")}
              data-testid="documents-tab-deleted"
              className={`px-3 py-1.5 text-[12px] uppercase tracking-widest border-b-2 -mb-px inline-flex items-center gap-1 ${
                isDeletedTab
                  ? "border-coach-blue text-carbon"
                  : "border-transparent text-steel hover:text-carbon"
              }`}
            >
              <Trash2 size={12} /> Deleted Documents
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-steel" />
            <input
              type="text"
              placeholder="Search by title…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-8 pr-3 h-9 text-[13px] rounded-md bg-white border border-coach-silver text-carbon w-64"
              data-testid="documents-search"
            />
          </div>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="h-9 text-[13px] rounded-md bg-white border border-coach-silver text-carbon px-2"
            data-testid="documents-type-filter"
          >
            <option value="">All types</option>
            <option value="quote_pdf">Quotes</option>
            <option value="contract_pdf">Contracts</option>
            <option value="pickup_sheet_pdf">Pickup Sheets</option>
            <option value="other">Other</option>
          </select>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="h-9 text-[13px] rounded-md bg-white border border-coach-silver text-carbon px-2"
            data-testid="documents-sort"
            aria-label="Sort documents by"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value} data-testid={`documents-sort-opt-${o.value}`}>{o.label}</option>
            ))}
          </select>
        </div>

        <div className="bg-white border border-coach-silver rounded-md overflow-hidden">
          <div className="px-4 pt-3">
            <SectionHeader testid={isDeletedTab ? "documents-section-deleted" : "documents-section-index"}>
              {isDeletedTab ? "Deleted Documents" : "Document Index"}
            </SectionHeader>
          </div>

          {/* ============ DESKTOP TABLE (≥768px) ============ */}
          <div className="hidden md:block">
            <table className="w-full text-[13px]" data-testid="documents-table">
              <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
                <tr>
                  <th className="text-left px-4 py-2">Title</th>
                  <th className="text-left px-4 py-2 w-32">Type</th>
                  <th className="text-left px-4 py-2 w-16">Version</th>
                  <th className="text-left px-4 py-2 w-24">Status</th>
                  <th className="text-left px-4 py-2 w-44" data-testid="documents-col-created-by">Created By</th>
                  <th className="text-left px-4 py-2 w-40" data-testid="documents-col-created-on">Created On</th>
                  <th className="text-right px-4 py-2 w-36"></th>
                </tr>
              </thead>
              <tbody>
                {rows === null && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-steel">Loading…</td></tr>
                )}
                {rows && rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-steel">
                      {isDeletedTab ? "No deleted documents." : "No documents match your filters."}
                    </td>
                  </tr>
                )}
                {rows && rows.map((d) => {
                  const createdByLabel = composeCreatedBy(d.created_by_display_name, d.is_former_user);
                  const createdOnShort = formatCreatedOn(d.created_at);
                  const createdOnFull = formatCreatedOnFull(d.created_at);
                  return (
                  <tr key={d.id} data-testid={`documents-row-${d.id}`} className="border-t border-coach-silver/60 hover:bg-road-white">
                    <td className="px-4 py-2 text-carbon">
                      <div>{d.title}</div>
                    </td>
                    <td className="px-4 py-2 text-steel">{TYPE_LABEL[d.document_type] || d.document_type}</td>
                    <td className="px-4 py-2 text-steel tabular-nums">v{d.version || 1}</td>
                    <td className="px-4 py-2 text-steel">{(d.status || "").toUpperCase()}</td>
                    <td
                      className="px-4 py-2"
                      data-testid={`documents-row-created-by-${d.id}`}
                      data-is-former-user={d.is_former_user ? "true" : "false"}
                    >
                      <span className={d.is_former_user ? "italic text-steel" : "text-carbon"}>
                        {createdByLabel}
                      </span>
                    </td>
                    <td
                      className="px-4 py-2 text-steel"
                      data-testid={`documents-row-created-on-${d.id}`}
                    >
                      <span title={createdOnFull} aria-label={createdOnFull}>
                        {createdOnShort}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">
                      <button
                        className="text-coach-blue hover:underline inline-flex items-center gap-1 text-[12px] mr-3"
                        onClick={() => openFile(d.file_id)}
                        data-testid={`documents-view-${d.id}`}
                      >
                        View <ExternalLink size={12} />
                      </button>
                      {canManageArchive && !isDeletedTab && (
                        <button
                          className="text-critical-red hover:underline inline-flex items-center gap-1 text-[12px] disabled:opacity-50"
                          onClick={() => requestDelete(d)}
                          disabled={busyId === d.id}
                          data-testid={`documents-delete-${d.id}`}
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      )}
                      {canManageArchive && isDeletedTab && (
                        <button
                          className="text-coach-blue hover:underline inline-flex items-center gap-1 text-[12px] disabled:opacity-50"
                          onClick={() => restoreDoc(d)}
                          disabled={busyId === d.id}
                          data-testid={`documents-restore-${d.id}`}
                        >
                          <RotateCcw size={12} /> Restore
                        </button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ============ MOBILE CARDS (<768px) ============ */}
          {/* RC10r Pass 2 · DI24 — proper card layout per Owner brief.
              Previous implementation hid desktop columns and inlined
              attribution under the title; Owner required stacked
              cards with Title / Type / Status / Created By / Created
              On / actions each on their own line. No horizontal
              overflow at 390×844. */}
          <div className="md:hidden" data-testid="documents-mobile-cards">
            {rows === null && (
              <div className="px-4 py-8 text-center text-steel text-[13px]">Loading…</div>
            )}
            {rows && rows.length === 0 && (
              <div className="px-4 py-8 text-center text-steel text-[13px]">
                {isDeletedTab ? "No deleted documents." : "No documents match your filters."}
              </div>
            )}
            {rows && rows.map((d) => {
              const createdByLabel = composeCreatedBy(d.created_by_display_name, d.is_former_user);
              const createdOnShort = formatCreatedOn(d.created_at);
              const createdOnFull = formatCreatedOnFull(d.created_at);
              return (
                <div
                  key={d.id}
                  data-testid={`documents-card-${d.id}`}
                  className="border-t border-coach-silver/60 px-4 py-3 bg-white"
                >
                  <div className="text-[14px] font-medium text-carbon break-words" data-testid={`documents-card-title-${d.id}`}>
                    {d.title}
                  </div>
                  <div className="mt-2 text-[12px]" data-testid={`documents-card-type-row-${d.id}`}>
                    <span className="text-steel uppercase tracking-widest text-[10px]">Type</span>
                    <div className="text-carbon" data-testid={`documents-card-type-${d.id}`}>
                      {TYPE_LABEL[d.document_type] || d.document_type}
                    </div>
                  </div>
                  <div className="mt-2 text-[12px]" data-testid={`documents-card-version-row-${d.id}`}>
                    <span className="text-steel uppercase tracking-widest text-[10px]">Version</span>
                    <div className="text-carbon tabular-nums" data-testid={`documents-card-version-${d.id}`}>
                      v{d.version || 1}
                    </div>
                  </div>
                  <div className="mt-2 text-[12px]" data-testid={`documents-card-status-row-${d.id}`}>
                    <span className="text-steel uppercase tracking-widest text-[10px]">Status</span>
                    <div className="text-carbon uppercase tracking-wide" data-testid={`documents-card-status-${d.id}`}>
                      {(d.status || "").toUpperCase() || "—"}
                    </div>
                  </div>
                  <div
                    className="mt-2 text-[12px]"
                    data-testid={`documents-card-created-by-${d.id}`}
                    data-is-former-user={d.is_former_user ? "true" : "false"}
                  >
                    <span className="text-steel uppercase tracking-widest text-[10px]">Created By</span>
                    <div className={d.is_former_user ? "italic text-steel" : "text-carbon"}>
                      {createdByLabel}
                    </div>
                  </div>
                  <div className="mt-2 text-[12px]" data-testid={`documents-card-created-on-${d.id}`}>
                    <span className="text-steel uppercase tracking-widest text-[10px]">Created On</span>
                    <div className="text-carbon" title={createdOnFull} aria-label={createdOnFull}>
                      {createdOnShort || "—"}
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <button
                      className="text-coach-blue hover:underline inline-flex items-center gap-1 text-[12.5px]"
                      onClick={() => openFile(d.file_id)}
                      data-testid={`documents-card-view-${d.id}`}
                    >
                      View <ExternalLink size={12} />
                    </button>
                    {canManageArchive && !isDeletedTab && (
                      <button
                        className="text-critical-red hover:underline inline-flex items-center gap-1 text-[12.5px] disabled:opacity-50"
                        onClick={() => requestDelete(d)}
                        disabled={busyId === d.id}
                        data-testid={`documents-card-delete-${d.id}`}
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    )}
                    {canManageArchive && isDeletedTab && (
                      <button
                        className="text-coach-blue hover:underline inline-flex items-center gap-1 text-[12.5px] disabled:opacity-50"
                        onClick={() => restoreDoc(d)}
                        disabled={busyId === d.id}
                        data-testid={`documents-card-restore-${d.id}`}
                      >
                        <RotateCcw size={12} /> Restore
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* ============ PAGINATION BAR ============ */}
          <DocumentsPaginationBar
            page={envelope.page}
            totalPages={envelope.total_pages}
            totalItems={envelope.total_items}
            pageSize={envelope.page_size}
            hasPrevious={envelope.has_previous}
            hasNext={envelope.has_next}
            firstRow={firstRow}
            lastRow={lastRow}
            onPage={setPage}
            onPageSize={setPageSize}
          />
        </div>

        {/* RC5 Item 5 · recoverable-removal confirmation modal. */}
        {confirmDoc && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-carbon/40"
            data-testid="documents-delete-confirm"
            role="dialog"
            aria-modal="true"
          >
            <div className="bg-white border border-coach-silver rounded-md shadow-lg w-full max-w-md mx-4 p-5">
              <div className="text-[14px] font-semibold text-carbon mb-2">
                Remove document?
              </div>
              <div className="text-[12.5px] text-carbon leading-relaxed" data-testid="documents-delete-confirm-copy">
                {confirmProtected ? (
                  <>
                    This will remove the item from the active Document Center.
                    It will remain recoverable in Deleted Documents and can be restored.
                    Underlying {confirmProtectedLabel} records are preserved for system operation.
                  </>
                ) : (
                  <>
                    This will remove &quot;{confirmDoc.title}&quot; from the active
                    Document Center. It will remain recoverable in Deleted Documents
                    and can be restored.
                  </>
                )}
              </div>
              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  className="px-3 py-1.5 text-[12.5px] border border-coach-silver text-carbon rounded-sm hover:bg-road-white"
                  onClick={() => setConfirmDoc(null)}
                  data-testid="documents-delete-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-3 py-1.5 text-[12.5px] font-medium bg-critical-red text-white rounded-sm hover:brightness-110 disabled:opacity-60"
                  onClick={() => performDelete(confirmDoc)}
                  disabled={busyId === confirmDoc.id}
                  data-testid="documents-delete-confirm-btn"
                >
                  {busyId === confirmDoc.id ? "Removing…" : "Remove"}
                </button>
              </div>
            </div>
          </div>
        )}
      </PageShell>
  );
}

// RC10r Pass 2 · Pagination bar — RC10n Fleet pattern applied to the
// Documents index. First / Prev / page numbers / Next / Last + page
// size dropdown. URL-backed via `?page=&page_size=`. Default 25.
function DocumentsPaginationBar({
  page, totalPages, totalItems, pageSize,
  hasPrevious, hasNext, firstRow, lastRow,
  onPage, onPageSize,
}) {
  const showFirstLast = totalPages > 3;
  const pages = [];
  const push = (n) => { if (n >= 1 && n <= totalPages && !pages.includes(n)) pages.push(n); };
  push(1); push(page - 1); push(page); push(page + 1); push(totalPages);
  pages.sort((a, b) => a - b);
  return (
    <div
      data-testid="documents-pagination"
      className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 px-4 py-3 border-t border-coach-silver bg-road-white rounded-b-md text-[12px]"
    >
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-steel" data-testid="documents-pagination-status">
          {totalItems === 0
            ? "No documents"
            : `Showing ${firstRow}\u2013${lastRow} of ${totalItems.toLocaleString()} documents`}
        </span>
        <label className="flex items-center gap-1.5 text-steel">
          Rows per page
          <select
            data-testid="documents-page-size-select"
            value={typeof pageSize === "string" ? pageSize : String(pageSize)}
            onChange={(e) => onPageSize(e.target.value)}
            className="border border-coach-silver rounded-sm px-2 py-0.5 text-[12px] bg-white"
          >
            {PAGE_SIZE_OPTIONS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </label>
      </div>
      <div className="flex items-center gap-1">
        {showFirstLast && (
          <button
            type="button"
            disabled={!hasPrevious}
            onClick={() => onPage(1)}
            data-testid="documents-page-first"
            className="px-1.5 py-1 border border-coach-silver rounded-sm bg-white hover:bg-coach-silver/20 disabled:opacity-40"
          ><ChevronFirst size={14} /></button>
        )}
        <button
          type="button"
          disabled={!hasPrevious}
          onClick={() => onPage(page - 1)}
          data-testid="documents-page-prev"
          className="px-1.5 py-1 border border-coach-silver rounded-sm bg-white hover:bg-coach-silver/20 disabled:opacity-40"
        ><ChevronLeft size={14} /></button>
        {pages.map((n, idx) => {
          const prev = pages[idx - 1];
          const gap = prev !== undefined && n - prev > 1;
          return (
            <React.Fragment key={n}>
              {gap && <span className="px-1 text-steel">…</span>}
              <button
                type="button"
                onClick={() => onPage(n)}
                disabled={n === page}
                data-testid={`documents-page-${n}`}
                className={`px-2 py-0.5 rounded-sm border text-[12px] ${
                  n === page
                    ? "bg-coach-blue text-white border-coach-blue"
                    : "bg-white border-coach-silver hover:bg-coach-silver/20"
                }`}
              >{n}</button>
            </React.Fragment>
          );
        })}
        <button
          type="button"
          disabled={!hasNext}
          onClick={() => onPage(page + 1)}
          data-testid="documents-page-next"
          className="px-1.5 py-1 border border-coach-silver rounded-sm bg-white hover:bg-coach-silver/20 disabled:opacity-40"
        ><ChevronRight size={14} /></button>
        {showFirstLast && (
          <button
            type="button"
            disabled={!hasNext}
            onClick={() => onPage(totalPages)}
            data-testid="documents-page-last"
            className="px-1.5 py-1 border border-coach-silver rounded-sm bg-white hover:bg-coach-silver/20 disabled:opacity-40"
          ><ChevronLast size={14} /></button>
        )}
      </div>
    </div>
  );
}
